import * as React from "react";
import ReactMarkdown, { type Options } from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkGfm from "remark-gfm";
import remarkHtml from "remark-html";

import { CitationUnderline } from "./CitationUnderline";
import { PdfSourcePreview } from "./PdfSourcePreview";
import { resolveCitationDocumentUrl } from "../services/gcpFileUrl";

const remarkPlugins: Options["remarkPlugins"] = [remarkGfm, remarkHtml];
const rehypePlugins: Options["rehypePlugins"] = [rehypeRaw];
const LOG_CHUNK_SIZE = 4000;

const columnWidths: Record<string, string> = {
  "Business Summary": "400px",
  "Acquired Company Description": "400px",
  Description: "400px",
  "All Investors": "400px",
  "Lead Investors": "300px",
  Experience: "300px",
  Location: "150px",
  LinkedIn: "200px",
};

export interface SimpleMarkdownProps {
  text: string;
  className?: string;
  tableClassName?: string;
  tdClassName?: string;
  thClassName?: string;
  compact?: boolean;
  onCitationClick?: (id: string, event: React.MouseEvent<HTMLElement>) => void;
  activeCitationId?: string | null;
}

type CitationUnderlineProps = {
  id?: string;
  children?: React.ReactNode;
  "data-datasource"?: string;
  "data-type"?: string;
};

type AnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  children?: React.ReactNode;
};

type IframeProps = React.IframeHTMLAttributes<HTMLIFrameElement> & {
  children?: React.ReactNode;
};

function normalizeMarkdownText(input: string): string {
  const normalized = input
    .replace(/\\r\\n|\\n|\\r/g, "\n")
    .replace(/\r\n?/g, "\n")
    .replace(/<br\s*\/?>/gi, "\n");

  return normalized
    .split("\n")
    .map(normalizeCollapsedPipeTableLine)
    .join("\n");
}

function normalizeCollapsedPipeTableLine(line: string): string {
  const trimmed = line.trim();
  if (!trimmed.startsWith("|")) return line;
  if ((trimmed.match(/\|/g) || []).length < 8) return line;
  if (!/\|\s*:?-{3,}:?\s*\|/.test(trimmed)) return line;

  const cells = splitPipeCells(trimmed);
  const separator = findSeparatorRun(cells);
  if (!separator) return line;

  const { start, length } = separator;
  if (start < length) return line;

  const headerCells = cells.slice(start - length, start);
  const separatorCells = cells.slice(start, start + length);
  const bodyCells = cells.slice(start + length);
  const rows: string[] = [];

  rows.push(formatMarkdownRow(headerCells));
  rows.push(formatMarkdownRow(separatorCells));

  for (let index = 0; index < bodyCells.length; index += length) {
    const row = bodyCells.slice(index, index + length);
    if (row.every((cell) => !cell)) continue;
    rows.push(formatMarkdownRow(normalizeRowWidth(row, length)));
  }

  return rows.length > 2 ? rows.join("\n") : line;
}

function splitPipeCells(line: string): string[] {
  let normalized = line.trim();
  if (normalized.startsWith("|")) normalized = normalized.slice(1);
  if (normalized.endsWith("|")) normalized = normalized.slice(0, -1);
  return normalized.split("|").map((cell) => cell.trim());
}

function findSeparatorRun(cells: string[]): { start: number; length: number } | null {
  let index = 0;
  while (index < cells.length) {
    if (!isSeparatorCell(cells[index])) {
      index++;
      continue;
    }

    const start = index;
    while (index < cells.length && isSeparatorCell(cells[index])) {
      index++;
    }

    const length = index - start;
    if (length >= 2) return { start, length };
  }

  return null;
}

function isSeparatorCell(cell: string): boolean {
  return /^:?-{3,}:?$/.test(cell);
}

function normalizeRowWidth(row: string[], width: number): string[] {
  if (row.length === width) return row;
  if (row.length > width) {
    return [...row.slice(0, width - 1), row.slice(width - 1).join(" | ")];
  }
  return [...row, ...new Array(width - row.length).fill("")];
}

function formatMarkdownRow(cells: string[]): string {
  return `| ${cells.join(" | ")} |`;
}

function isPdfUrl(value: string): boolean {
  return /\.pdf(?:[?#]|$)/i.test(value);
}

function logLongString(label: string, value: string, metadata?: Record<string, unknown>): void {
  const chunkCount = Math.max(1, Math.ceil(value.length / LOG_CHUNK_SIZE));
  console.log(`${label} (${value.length} chars, ${chunkCount} chunks)`, metadata ?? {});
  for (let index = 0; index < chunkCount; index++) {
    const start = index * LOG_CHUNK_SIZE;
    console.log(`${label} chunk ${index + 1}/${chunkCount}`, value.slice(start, start + LOG_CHUNK_SIZE));
  }
}

export const SimpleMarkdown: React.FC<SimpleMarkdownProps> = ({
  text,
  className,
  tableClassName,
  tdClassName,
  thClassName,
  compact = true,
  onCitationClick,
  activeCitationId = null,
}) => {
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const normalizedText = React.useMemo(() => normalizeMarkdownText(text), [text]);

  React.useEffect(() => {
    logLongString("[CitationRenderer] SimpleMarkdown raw input", text, { className });
    logLongString("[CitationRenderer] SimpleMarkdown normalized input", normalizedText, {
      className,
      changedByNormalizer: normalizedText !== text,
    });
  }, [className, normalizedText, text]);

  React.useEffect(() => {
    if (!activeCitationId) return undefined;

    let frameId = 0;
    let attempts = 0;
    const maxAttempts = 24;

    const scrollToCitation = () => {
      const root = rootRef.current;
      if (!root) return;

      const targets = Array.from(root.querySelectorAll<HTMLElement>("[data-citation-id]"));
      const target = targets.find((element) => element.dataset.citationId === activeCitationId);
      if (!target) {
        attempts += 1;
        if (attempts < maxAttempts) {
          frameId = window.requestAnimationFrame(scrollToCitation);
        }
        return;
      }

      target.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
    };

    frameId = window.requestAnimationFrame(scrollToCitation);

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [activeCitationId, normalizedText]);

  const components: Options["components"] = {
    table: ({ ...props }) => (
      <div
        className={[
          compact ? "simple-markdown-table-scroll max-w-full overflow-x-auto rounded-lg" : undefined,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <table
          className={[
            "w-full border-collapse overflow-hidden rounded-lg border border-border",
            compact ? "min-w-full table-fixed text-[11px] leading-tight" : "text-sm",
            tableClassName,
          ]
            .filter(Boolean)
            .join(" ")}
          {...props}
        />
      </div>
    ),
    thead: ({ ...props }) => (
      <thead
        className="bg-background-secondary text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground"
        {...props}
      />
    ),
    th: ({ children: thChildren, ...props }) => {
      const textContent = typeof thChildren === "string" ? thChildren : "";
      const hasColumnWidth = textContent in columnWidths;
      const style = {
        ...props.style,
        ...(hasColumnWidth && {
          minWidth: columnWidths[textContent as keyof typeof columnWidths],
        }),
      };

      return (
        <th
          className={[
            "border-b border-border text-left font-semibold uppercase tracking-wide text-muted-foreground",
            compact ? "px-1.5 py-1 text-[10px] leading-tight" : "px-4 py-2.5 text-xs",
            thClassName,
          ]
            .filter(Boolean)
            .join(" ")}
          {...props}
          style={style}
        >
          {thChildren}
        </th>
      );
    },
    tr: ({ ...props }) => (
      <tr
        className="border-b border-border transition-colors last:border-b-0 hover:bg-secondary/50"
        {...props}
      />
    ),
    td: ({ ...props }) => (
      <td
        className={[
          compact ? "px-1.5 py-1 text-[11px] leading-snug" : "px-4 py-2.5 text-sm",
          "text-foreground",
          tdClassName,
        ]
          .filter(Boolean)
          .join(" ")}
        {...props}
      />
    ),
    a: (props) => {
      const { children, ...anchorProps } = props as AnchorProps;
      return (
        <a {...anchorProps} target="_blank" rel="noopener noreferrer">
          {children}
        </a>
      );
    },
    u: (props) => {
      const underlineProps = props as CitationUnderlineProps;
      const id = underlineProps.id ?? "";
      const dataSource = underlineProps["data-datasource"];
      const dataType = underlineProps["data-type"];
      const hasFinsterCitation = Boolean(id || dataSource || dataType);

      if (!hasFinsterCitation) {
        return <u>{underlineProps.children}</u>;
      }

      const citation = (
        <CitationUnderline
          id={id}
          dataSource={dataSource}
          dataType={dataType}
          onClick={onCitationClick}
        >
          {underlineProps.children}
        </CitationUnderline>
      );

      if (id && id === activeCitationId) {
        return (
          <span
            className="citation-html-active-target"
            data-active-citation="true"
            aria-label="Active citation"
          >
            {citation}
          </span>
        );
      }

      return citation;
    },
    iframe: (props) => {
      const { children: _children, ...iframeProps } = props as IframeProps;
      if (!iframeProps.src) return null;
      const resolvedSrc = resolveCitationDocumentUrl(iframeProps.src);
      if (isPdfUrl(iframeProps.src)) {
        return (
          <div className="my-4">
            <PdfSourcePreview
              href={resolvedSrc}
              title={iframeProps.title ?? "Embedded PDF source"}
              highlights={[]}
              initialHighlightId={null}
            />
          </div>
        );
      }

      const iframeClassName = [
        "my-4 min-h-[520px] w-full rounded-md border border-border bg-background",
        iframeProps.className ?? "",
      ].join(" ").trim();
      return (
        <iframe
          {...iframeProps}
          src={resolvedSrc}
          title={iframeProps.title ?? "Embedded source"}
          className={iframeClassName}
        />
      );
    },
    h1: ({ children }) => (
      <h1
        className={`${compact ? "mb-1.5 pt-0.5 text-[14px] leading-snug" : "mb-3 pt-4 text-2xl"} font-bold text-foreground`}
      >
        {children}
      </h1>
    ),
    h2: ({ children }) => (
      <h2
        className={`${compact ? "mb-1.5 pt-1 text-[13px] leading-snug" : "mb-3 pt-4 text-xl"} font-semibold text-foreground`}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3
        className={`${compact ? "mb-1 pt-1 text-[12.5px] leading-snug" : "mb-2 pt-3 text-lg"} font-semibold text-foreground`}
      >
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4
        className={`${compact ? "mb-1 pt-0.5 text-[12px] leading-snug" : "mb-2 pt-2 text-base"} font-semibold text-foreground`}
      >
        {children}
      </h4>
    ),
    p: ({ children }) => (
      <div
        className={`${compact ? "mb-1.5 text-[12.5px] leading-[17px]" : "mb-3 text-sm leading-relaxed"} text-foreground`}
      >
        {children}
      </div>
    ),
    ul: ({ children }) => (
      <ul
        className={`${compact ? "mb-1.5 space-y-0.5 pl-4 text-[12.5px]" : "mb-3 space-y-1 pl-6 text-sm"} list-disc text-foreground`}
      >
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol
        className={`${compact ? "mb-1.5 space-y-0.5 pl-4 text-[12.5px]" : "mb-3 space-y-1 pl-6 text-sm"} list-decimal text-foreground`}
      >
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
  };

  return (
    <div
      ref={rootRef}
      className={[className, compact ? "simple-markdown--compact" : undefined]
        .filter(Boolean)
        .join(" ")}
      style={{
        wordWrap: "break-word",
        overflowWrap: "break-word",
        wordBreak: "break-word",
        minWidth: 0,
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <ReactMarkdown
        remarkPlugins={remarkPlugins}
        rehypePlugins={rehypePlugins}
        components={components}
      >
        {normalizedText}
      </ReactMarkdown>
    </div>
  );
};
